import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLoader, FiSend, FiZap } from 'react-icons/fi';

import {
  type AnswerConclusion,
  type AnswerSuggestionVariant,
  type StructuredAnswer,
  useSuggestAnswer,
} from '../../../helpers/api/discussions/discussions';
import { ConclusionChip } from './StructuredAnswerBlock';
import { CONCLUSION_CHIP_IDLE } from './collaboration-badges';

const CONCLUSION_OPTIONS: AnswerConclusion[] = [
  'AGREE',
  'DISAGREE',
  'UNCERTAIN',
  'NEED_MORE_INFO',
];

const CONCLUSION_I18N: Record<AnswerConclusion, string> = {
  AGREE: 'conclusion.agree',
  DISAGREE: 'conclusion.disagree',
  UNCERTAIN: 'conclusion.uncertain',
  NEED_MORE_INFO: 'conclusion.need_more_info',
};

const CONCLUSION_CHIP: Record<
  AnswerConclusion,
  { active: string; idle: string }
> = {
  AGREE: {
    active: 'border-emerald-600 bg-emerald-600 text-white',
    idle: CONCLUSION_CHIP_IDLE.AGREE,
  },
  DISAGREE: {
    active: 'border-red-600 bg-red-600 text-white',
    idle: CONCLUSION_CHIP_IDLE.DISAGREE,
  },
  UNCERTAIN: {
    active: 'border-amber-500 bg-amber-500 text-white',
    idle: CONCLUSION_CHIP_IDLE.UNCERTAIN,
  },
  NEED_MORE_INFO: {
    active: 'border-blue-600 bg-blue-600 text-white',
    idle: CONCLUSION_CHIP_IDLE.NEED_MORE_INFO,
  },
};

type ReviewReplyComposerProps = {
  threadId: string;
  mode: 'review' | 'comment';
  isPosting: boolean;
  llmEnabled: boolean;
  onReply: (payload: { content: string; answer?: StructuredAnswer }) => Promise<void>;
};

function applyVariant(variant: AnswerSuggestionVariant) {
  return {
    content: variant.suggested_content,
    conclusion: (variant.answer.conclusion ?? '') as AnswerConclusion | '',
  };
}

export default function ReviewReplyComposer({
  threadId,
  mode,
  isPosting,
  llmEnabled,
  onReply,
}: ReviewReplyComposerProps) {
  const { t } = useTranslation('ClinicalCollaboration');
  const suggestMutation = useSuggestAnswer(threadId);
  const showConclusions = mode === 'review';

  const [conclusion, setConclusion] = useState<AnswerConclusion | ''>('');
  const [content, setContent] = useState('');
  const [aiDraftActive, setAiDraftActive] = useState(false);
  const [draftOptions, setDraftOptions] = useState<AnswerSuggestionVariant[]>([]);
  const [selectedDraftIndex, setSelectedDraftIndex] = useState<number | null>(null);

  const isSuggesting = suggestMutation.isPending;
  const canSubmit = content.trim().length > 0 && !isPosting && !isSuggesting;

  const handleSuggest = async () => {
    try {
      const result = await suggestMutation.mutateAsync();
      if (!result?.suggestions?.length) return;
      setDraftOptions(result.suggestions);
      const first = applyVariant(result.suggestions[0]);
      setContent(first.content);
      if (showConclusions) {
        setConclusion(first.conclusion);
      }
      setSelectedDraftIndex(0);
      setAiDraftActive(true);
    } catch {
      // toast handled in hook
    }
  };

  const handlePickDraft = (index: number) => {
    const variant = draftOptions[index];
    if (!variant) return;
    const picked = applyVariant(variant);
    setContent(picked.content);
    if (showConclusions) {
      setConclusion(picked.conclusion);
    }
    setSelectedDraftIndex(index);
    setAiDraftActive(true);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const message = content.trim();
    await onReply({
      content: message,
      ...(showConclusions && conclusion ? { answer: { conclusion } } : {}),
    });
    setConclusion('');
    setContent('');
    setAiDraftActive(false);
    setSelectedDraftIndex(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const toggleConclusion = (value: AnswerConclusion) => {
    setConclusion((prev) => (prev === value ? '' : value));
    setSelectedDraftIndex(null);
  };

  return (
    <div className="space-y-2">
      {aiDraftActive && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/50 dark:text-amber-200">
          {t('aiDraftDisclaimer')}
        </p>
      )}

      {draftOptions.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium text-slate-500 dark:text-gray-400">
            {draftOptions.length > 1 ? t('pickDraft') : t('aiDraftReady')}
          </p>
          <div className="flex flex-col gap-1.5">
            {draftOptions.map((variant, index) => {
              const selected = selectedDraftIndex === index;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handlePickDraft(index)}
                  className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                    selected
                      ? 'border-purple-400 bg-purple-50 dark:border-purple-600 dark:bg-purple-900/50'
                      : 'border-slate-200 bg-white hover:border-slate-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="mb-1 flex items-center gap-2">
                    {variant.answer.conclusion && (
                      <ConclusionChip conclusion={variant.answer.conclusion} />
                    )}
                    <span className="text-[10px] text-slate-400 dark:text-gray-500">
                      {t('draftOption', { number: index + 1 })}
                    </span>
                  </div>
                  <p className="line-clamp-2 text-slate-700 dark:text-gray-200">
                    {variant.suggested_content}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <textarea
        value={content}
        onChange={(e) => {
          setContent(e.target.value);
          setSelectedDraftIndex(null);
        }}
        onKeyDown={handleKeyDown}
        disabled={isPosting || isSuggesting}
        rows={2}
        placeholder={t('replyPlaceholder')}
        className="min-h-[44px] w-full resize-none rounded-xl border border-slate-200 bg-gray-50 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-500"
      />

      {showConclusions && (
      <div className="flex flex-wrap gap-1.5">
        {CONCLUSION_OPTIONS.map((value) => {
          const selected = conclusion === value;
          const styles = CONCLUSION_CHIP[value];
          return (
            <button
              key={value}
              type="button"
              disabled={isPosting || isSuggesting}
              onClick={() => toggleConclusion(value)}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                selected ? styles.active : styles.idle
              }`}
            >
              {t(CONCLUSION_I18N[value])}
            </button>
          );
        })}
      </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => void handleSuggest()}
          disabled={!llmEnabled || isSuggesting || isPosting}
          title={llmEnabled ? undefined : t('suggestUnavailable')}
          className="flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-800 transition-colors hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-purple-600 dark:bg-purple-900/50 dark:text-purple-200 dark:hover:bg-purple-900/70"
        >
          {isSuggesting ? (
            <FiLoader className="animate-spin" size={14} />
          ) : (
            <FiZap size={14} />
          )}
          {isSuggesting
            ? t('suggesting')
            : draftOptions.length > 0
              ? t('regenerateAnswer')
              : t('suggestAnswer')}
        </button>
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {isPosting ? <FiLoader className="animate-spin" size={14} /> : <FiSend size={14} />}
          {t('reply')}
        </button>
      </div>
    </div>
  );
}
